import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Menu, X, Info, UserRoundCheck, Star, ShieldCheck, CheckSquare, Square } from "lucide-react";
import { BIZ, SERVICES, ABOUT, REPUTATION } from "./talabaData";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import AccountingInquiryForm from "@/components/AccountingInquiryForm";

const INDIGO = "#3730A3";
const ORANGE = "#EA580C";
const CREAM = "#F7F6FB";
const INK = "#211F33";
const SLATE = "#565270";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

// A checklist/utility-app interaction — pick the services you need, see a running count —
// matches the firm's own "smart & practical solutions" positioning more literally than cards.
export default function TalabaSite() {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState({});
  const tagalog = typeof window !== "undefined" && window.localStorage.getItem("mysitecare-language") === "tl";
  const links = [["Services", "#services"], ["About", "#about"], ["Contact", "#contact"]];
  const toggle = (title) => setChecked((c) => ({ ...c, [title]: !c[title] }));
  const selectedCount = Object.values(checked).filter(Boolean).length;

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Inter', sans-serif" }} data-testid="talaba-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      <div data-testid="accounting-disclaimer" className="bg-black text-white text-center text-xs py-2.5 px-4">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: ORANGE }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 shadow-sm" style={{ background: INDIGO }}>
        <nav className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="talaba-logo" className="flex items-center gap-2 text-white font-bold text-base">
            <UserRoundCheck size={20} style={{ color: "#FDBA74" }} /> R. Talaba Accounting
          </a>
          <div className="hidden md:flex items-center gap-7">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-white/75 hover:text-white transition-colors">{l}</a>)}
          </div>
          <a href={`tel:${BIZ.phone}`} data-testid="talaba-header-cta" className="hidden sm:inline-flex rounded-full px-5 py-2 text-sm font-semibold" style={{ background: ORANGE, color: "white" }}>Call Now</a>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="talaba-menu-toggle" aria-label="Menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1" style={{ background: INDIGO }}>{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium text-white/85">{l}</a>)}</div>}
      </header>

      <section className="py-12 sm:py-16" style={{ background: "#F1EEF8" }} data-testid="talaba-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid md:grid-cols-[1fr_0.48fr]">
          <div className="p-8 sm:p-12 lg:p-16 bg-white border-l-8" style={{ borderColor: ORANGE }}>
            <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: INDIGO }}>Mandaue City, Cebu</span>
            <motion.h1 {...fadeUp} className="mt-5 text-3xl sm:text-5xl font-extrabold leading-[1.08]" style={{ color: INDIGO }}>{BIZ.tagline}</motion.h1>
            <motion.p {...fadeUp} className="mt-5 max-w-lg" style={{ color: SLATE }}>Accounting, tax, registration and compliance support for Cebu businesses.</motion.p>
            <motion.div {...fadeUp} className="mt-8 flex flex-wrap gap-4">
              <a href="#contact" data-testid="talaba-hero-cta" className="px-7 py-3.5 text-sm font-semibold" style={{ background: ORANGE, color: "white" }}>Ask About Your Business</a>
              <a href={`tel:${BIZ.phone}`} data-testid="talaba-hero-call" className="px-7 py-3.5 text-sm font-semibold border" style={{ color: INDIGO, borderColor: "#C7C4DB" }}>Call {BIZ.phoneDisplay}</a>
            </motion.div>
          </div>
          <aside className="p-8 sm:p-10 flex flex-col justify-between" style={{ background: INDIGO, color: "white" }}>
            <UserRoundCheck size={34} style={{ color: "#FDBA74" }} />
            <div className="mt-14 md:mt-0">
              <p className="text-xs uppercase tracking-[0.18em] text-white/55">Arianne Talaba, CPA</p>
              <p className="mt-3 text-sm leading-7 text-white/80">Public records strongly associate the practice with Rabboni Seno Talaba, CPA. Current credentials should be confirmed directly.</p>
            </div>
          </aside>
        </div>
      </section>

      {/* SERVICES — interactive checklist, not a static card grid */}
      <section id="services" className="py-16">
        <div className="max-w-lg mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold text-center mb-2" style={{ color: INDIGO }}>What Do You Need?</motion.h2>
          <motion.p {...fadeUp} className="text-center text-sm mb-8" style={{ color: SLATE }}>Check what applies — bring the list when you reach out.</motion.p>
          <div className="bg-white rounded-lg shadow-sm divide-y" style={{ borderColor: CREAM }}>
            {SERVICES.map((s) => (
              <button key={s.title} onClick={() => toggle(s.title)} data-testid={`talaba-check-${s.title.toLowerCase().replace(/\s/g, "-")}`} className="w-full flex items-start gap-3 px-5 py-4 text-left" style={{ borderColor: "#EEECF7" }}>
                {checked[s.title] ? <CheckSquare size={20} style={{ color: ORANGE }} className="shrink-0 mt-0.5" /> : <Square size={20} style={{ color: "#C7C4DB" }} className="shrink-0 mt-0.5" />}
                <div>
                  <p className="font-semibold text-sm" style={{ color: INDIGO }}>{s.title}</p>
                  <p className="mt-0.5 text-xs" style={{ color: SLATE }}>{s.text}</p>
                </div>
              </button>
            ))}
          </div>
          <div className="mt-5 text-center text-sm font-semibold" style={{ color: selectedCount ? ORANGE : SLATE }}>
            {selectedCount
              ? (tagalog ? `${selectedCount} napili — banggitin ito kapag tumawag kayo` : `${selectedCount} selected — mention these when you call`)
              : (tagalog ? "Wala pang napili" : "Nothing selected yet")}
          </div>
        </div>
      </section>

      <section id="about" className="py-16" style={{ background: "#EAE7F5" }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold mb-5" style={{ color: INDIGO }}>About the Firm</motion.h2>
          <motion.p {...fadeUp} className="text-sm" style={{ color: SLATE }}>{ABOUT}</motion.p>
          <motion.div {...fadeUp} className="mt-5 flex items-start gap-2.5 rounded-lg p-4 text-xs bg-white text-left">
            <ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: ORANGE }} />
            Please confirm current accreditation status directly before relying on it.
          </motion.div>
        </div>
      </section>

      <section className="py-12" style={{ background: INDIGO }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-1.5 mb-2">
            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill={ORANGE} style={{ color: ORANGE }} />)}
          </div>
          <p className="text-lg font-bold text-white">{REPUTATION.rating}★ across {REPUTATION.reviewCountLabel}</p>
          <p className="mt-1.5 text-xs text-white/60">{REPUTATION.note}</p>
        </div>
      </section>

      <section id="contact" className="py-16" style={{ background: "#EAE7F5" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: INDIGO }}>Office</h2>
            <ul className="space-y-4 text-sm mb-6">
              <li className="flex items-start gap-3"><MapPin size={18} style={{ color: ORANGE }} className="mt-0.5 shrink-0" />{BIZ.address}</li>
              <li className="flex items-start gap-3"><Phone size={18} style={{ color: ORANGE }} className="mt-0.5 shrink-0" /><a href={`tel:${BIZ.phone}`} data-testid="talaba-phone-link" className="hover:underline">{BIZ.phoneDisplay}</a> / {BIZ.phoneAlt}</li>
              <li className="flex items-start gap-3"><Mail size={18} style={{ color: ORANGE }} className="mt-0.5 shrink-0" /><a href={`mailto:${BIZ.email}`} data-testid="talaba-email-link" className="hover:underline">{BIZ.email}</a></li>
            </ul>
            <div className="rounded-lg overflow-hidden shadow-sm min-h-[220px]">
              <iframe title="R. Talaba Accounting Firm map" src={BIZ.mapEmbed} className="w-full h-full min-h-[220px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <AccountingInquiryForm
              testidPrefix="talaba"
              businessName={BIZ.name}
              accent={INDIGO}
              accentText="#FFFFFF"
              phone={BIZ.phone}
              phoneDisplay={BIZ.phoneDisplay}
              email={BIZ.email}
            />
          </motion.div>
        </div>
      </section>

      <footer className="py-8" style={{ background: INDIGO }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
          <p className="font-bold text-white">R. Talaba Accounting Firm</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current details before visiting.</p>
        </div>
      </footer>

      <ConceptCTAPill slug="talaba-accounting-cebu-concept" />
    </div>
  );
}
